import dataSourceRegistry from '../data/dataSourceRegistry.json'
import regulatoryUpdates from '../data/regulatoryUpdates.json'

/**
 * Parse a YYYY-MM-DD string as local midnight (not UTC).
 * new Date('2025-06-15') is midnight UTC which shifts to prior day in western timezones.
 */
function parseLocalDate(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/**
 * Calculate the freshness status of each data source.
 */
export function calculateDataSourceHealth() {
  const now = new Date()
  const currentYear = now.getFullYear()

  // Dynamically update regulatory source with live metadata
  const sources = dataSourceRegistry.sources.map(s => {
    if (s.id === 'federal-register-regulatory' && regulatoryUpdates?.lastChecked) {
      return {
        ...s,
        lastIngested: regulatoryUpdates.lastChecked.split('T')[0],
        lastIngestedVersion: `${regulatoryUpdates.findings?.length || 0} active, ${regulatoryUpdates.archivedFindings?.length || 0} archived`
      }
    }
    return s
  })

  const enrichedSources = sources.map(source => {
    if (source.status === 'not-ingested' || !source.lastIngested) {
      return {
        ...source,
        calculatedStatus: 'not-ingested',
        statusInfo: dataSourceRegistry.statusDefinitions['not-ingested'],
        daysUntilExpected: null,
        daysSinceIngested: null,
        nextExpectedDate: null
      }
    }

    const lastIngestedDate = parseLocalDate(source.lastIngested)
    const daysSinceIngested = Math.floor((now - lastIngestedDate) / (1000 * 60 * 60 * 24))

    let nextExpectedDate = null

    if (source.refreshCadence === 'continuous') {
      const reminderDate = new Date(lastIngestedDate)
      reminderDate.setDate(reminderDate.getDate() + source.reminderLeadDays)
      nextExpectedDate = reminderDate
    } else if (source.expectedPublishMonths) {
      const months = source.expectedPublishMonths
      let found = false
      for (let yearOffset = 0; yearOffset <= 1 && !found; yearOffset++) {
        const year = currentYear + yearOffset
        for (const month of months) {
          const candidate = new Date(year, month - 1, 15)
          if (candidate > lastIngestedDate) {
            nextExpectedDate = candidate
            found = true
            break
          }
        }
      }
    } else if (source.expectedPublishMonth) {
      // For annual reports, the next expected publication is always the year after
      // ingestion (if ingested in or after the publish month) or the current year
      // of ingestion (if ingested before the publish month of that year).
      // We use a 60-day window: if ingested within 60 days before the publish date,
      // it's considered the same cycle.
      const publishMonth = source.expectedPublishMonth
      const candidate = new Date(lastIngestedDate.getFullYear(), publishMonth - 1, 15)
      const windowStart = new Date(candidate)
      windowStart.setDate(windowStart.getDate() - 60)

      if (lastIngestedDate >= windowStart) {
        // Ingested near or after this cycle's publish date — next is next year
        nextExpectedDate = new Date(lastIngestedDate.getFullYear() + 1, publishMonth - 1, 15)
      } else {
        // Ingested well before this cycle's publish date — this cycle is next
        nextExpectedDate = candidate
      }
    }

    let calculatedStatus = 'current'
    let daysUntilExpected = null

    if (nextExpectedDate) {
      daysUntilExpected = Math.floor((nextExpectedDate - now) / (1000 * 60 * 60 * 24))
      const overdueDate = new Date(nextExpectedDate)
      overdueDate.setDate(overdueDate.getDate() + (source.overdueGraceDays || 45))

      if (now > overdueDate) {
        calculatedStatus = 'overdue'
      } else if (now > nextExpectedDate) {
        calculatedStatus = 'due'
      } else if (daysUntilExpected <= (source.reminderLeadDays || 30)) {
        calculatedStatus = 'upcoming'
      }
    }

    return {
      ...source,
      calculatedStatus,
      statusInfo: dataSourceRegistry.statusDefinitions[calculatedStatus],
      daysUntilExpected,
      daysSinceIngested,
      nextExpectedDate: nextExpectedDate?.toISOString()?.split('T')[0] || null
    }
  })

  const statusOrder = { overdue: 0, due: 1, upcoming: 2, 'not-ingested': 3, current: 4 }
  enrichedSources.sort((a, b) => (statusOrder[a.calculatedStatus] ?? 5) - (statusOrder[b.calculatedStatus] ?? 5))

  const counts = enrichedSources.reduce((acc, s) => {
    acc[s.calculatedStatus] = (acc[s.calculatedStatus] || 0) + 1
    return acc
  }, {})

  let overallStatus = 'healthy'
  let overallColor = 'green'
  if (counts.overdue > 0) { overallStatus = 'action-required'; overallColor = 'red' }
  else if (counts.due > 0) { overallStatus = 'review-needed'; overallColor = 'yellow' }
  else if (counts.upcoming > 0) { overallStatus = 'updates-expected'; overallColor = 'blue' }

  return {
    sources: enrichedSources,
    summary: {
      total: enrichedSources.length,
      counts,
      overallStatus,
      overallColor,
      criticalOverdue: enrichedSources.filter(s => s.calculatedStatus === 'overdue' && s.priority === 'critical').length
    }
  }
}

/**
 * Get sources that need attention (due, overdue, or not-ingested with critical/high priority)
 */
export function getActionableSources() {
  const { sources } = calculateDataSourceHealth()
  return sources.filter(s =>
    s.calculatedStatus === 'overdue' ||
    s.calculatedStatus === 'due' ||
    (s.calculatedStatus === 'not-ingested' && (s.priority === 'critical' || s.priority === 'high'))
  )
}
